import { forwardRef } from 'react';
import { Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

interface SettingsIconButtonProps extends Omit<ButtonProps, 'children'> {
  tooltip?: string;
  showTooltip?: boolean;
}

/**
 * Standardized settings icon button.
 */
export const SettingsIconButton = forwardRef<HTMLButtonElement, SettingsIconButtonProps>(
  ({ tooltip, showTooltip = true, className, ...props }, ref) => {
    const { t } = useTranslation('common');
    const label = tooltip || t('nav.settings');

    const button = (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        aria-label={label}
        className={cn(className)}
        {...props}
      >
        <Settings className="h-4 w-4" />
      </Button>
    );

    if (!showTooltip) return button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }
);

SettingsIconButton.displayName = 'SettingsIconButton';
